<template>
  <v-card shaped v-if="internalEntries">
    <v-card-title>
      <h4 class="h4">{{getPeriodString}}</h4>
      <v-spacer></v-spacer>
      <span class="subtitle-1 primary--text" v-if="!validationTimeReached">
        {{getValidationString}}
      </span>
    </v-card-title>
    <v-card-text>
    <v-container>
      <v-row align="center">
        <v-col></v-col>
        <template v-for="(field, index) in fields">
          <v-col v-if="field.show || field.required" :key="'fieldHeader' + index">{{field.name}}</v-col>
        </template>
      </v-row>
      <v-divider horizontal class="my-3"></v-divider>
      <template v-for="(entry, index) in internalEntries">
        <EntryFormRow
          v-model="internalEntries[index]"
          :key="'row' + index"
          :fields="fields"
          :validation="$v.internalEntries.$each[index]"
          :suspiciousFields="suspiciousFields[index]"
          @edited="edited = true"
        />
      </template>
      <template>
        <v-divider class="my-3"></v-divider>
        <EntryFormTotalRow
          :entries="internalEntries"
          :fields="fields"
        />
      </template>
    </v-container>
    </v-card-text>
    <v-card-actions>
      <v-spacer></v-spacer>
      <v-btn
          color="primary"
          large
          @click="saveData"
          rounded
          outlined
          :disabled="hasBeenSent && !admin"
        >
        {{ $t('Entries.save') }}
        </v-btn>
        <v-btn
          v-if="canSend"
          color="success"
          outlined
          large
          @click="testAndSendData"
          rounded
          :disabled="!validationTimeReached"
          :loading="sending"
        >
          <v-icon left>send</v-icon>
          {{ $t('Entries.send') }}
        </v-btn>
    </v-card-actions>
    <ConfirmationModal v-if="showConfirmationModal"
                       :title="$t('Entries.areYouSure')"
                       :text="$t('Entries.someInputsAreSuspicious')"
                       :acceptText="$t('Entries.send')"
                       :accept="sendData"
                       :refuse="closeModal"
    />
  </v-card>
</template>

<script>
import { required, integer, decimal, minValue } from 'vuelidate/lib/validators'
import Entries from '@/services/entries'
import EntryFormRow from '@/components/Entry/EntryFormRow'
import EntryFormTotalRow from '@/components/Entry/EntryFormTotalRow'
import ConfirmationModal from "@/components/Utils/ConfirmationModal";
import { endOfWeek, isAfter, setDay, setHours, getWeek } from 'date-fns'
export default {
  name: 'EntryForm',
  components: { EntryFormRow, EntryFormTotalRow, ConfirmationModal },
  props: {
    entries: {type: Array, required: true},
    admin: {type: Boolean, required: false},
    user: {type: Object, required: true} // concerned user
  },
  data: () => ({
    internalEntries: [],
    suspiciousEntries: [],
    edited: false,
    sending: false,
    formValidationStatus: {
      invalid: 0,
      suspicious: 1,
      valid: 2
    },
    showConfirmationModal: false
  }),
  watch: {
    entries: {
      immediate: true,
      handler: function (value) {
        this.internalEntries = value.map(entry => this.formatEntry(entry))
        this.suspiciousFields
        this.edited = false
      }
    }
  },
  computed: {
    suspiciousFields() {
      return this.internalEntries.map((entry) => ({
        nbTransactions: (entry.hasNbTransactions && entry.nbTransactions <= 0),
        nbQuantity: (entry.hasNbQuantity && entry.nbQuantity <= 0),
        turnoverZero: (entry.hasTurnoverZero && (entry.turnoverZero <= 0 || entry.turnoverZero > 100000)),
        turnoverSuperReduced: (entry.hasTurnoverSuperReduced && (entry.turnoverSuperReduced <= 0 || entry.turnoverSuperReduced > 100000)),
        turnoverReduced: (entry.hasTurnoverReduced && (entry.turnoverReduced <= 0 || entry.turnoverReduced > 100000)),
        turnoverIntermediate: (entry.hasTurnoverIntermediate && (entry.turnoverIntermediate <= 0 || entry.turnoverIntermediate > 100000)),
        turnoverNormal: (entry.hasTurnoverNormal && (entry.turnoverNormal <= 0 || entry.turnoverNormal > 100000)),
      }));
    },
    hasSuspiciousField() {
      return !!this.suspiciousFields.find(entry => {
        return !!Object.values(entry).find(isSuspicious => isSuspicious === true);
      });
    },
    fields () {
      if (!this.entries.length) return []
      const turnoverType = this.entries[0].useTtc ? ' TTC ' : ' HT '
      return [
        { name: 'CA ' + turnoverType + this.entries[0].zeroRate + '%', key: 'turnoverZero', required: this.entries[0].hasTurnoverZero, type: 'ca', icon: 'euro' },
        { name: 'CA ' + turnoverType + this.entries[0].superReducedRate + '%', key: 'turnoverSuperReduced', required: this.entries[0].hasTurnoverSuperReduced, type: 'ca', icon: 'euro' },
        { name: 'CA ' + turnoverType + this.entries[0].reducedRate + '%', key: 'turnoverReduced', required: this.entries[0].hasTurnoverReduced, type: 'ca', icon: 'euro' },
        { name: 'CA ' + turnoverType + this.entries[0].intermediateRate + '%', key: 'turnoverIntermediate', required: this.entries[0].hasTurnoverIntermediate, type: 'ca', icon: 'euro' },
        { name: 'CA ' + turnoverType + this.entries[0].normalRate + '%', key: 'turnoverNormal', required: this.entries[0].hasTurnoverNormal, type: 'ca', icon: 'euro' },
        { name: 'Nombre d\'entrées', key: 'nbEntries', required: this.entries[0].hasNbEntries, type: 'number' },
        { name: 'Transactions', key: 'nbTransactions', required: this.entries[0].hasNbTransactions, type: 'number' },
        { name: 'Quantité vendue', key: 'nbQuantity', required: this.entries[0].hasNbQuantity, type: 'number' },
        { name: 'Commentaire', key: 'comment', show: true },
      ].filter(field => field.required || field.show)
    },

    firstEntryDate () {
      return this.entries[0].dateISO
    },

    hasBeenSent () {
      return this.entries.every(entry => entry.hasBeenSent)
    },

    canSend () {
      return !this.hasBeenSent && this.validationTimeReached
    },

    validationTime () {
      return this.entries[0] &&
        setDay(
          setHours(this.firstEntryDate, this.user.mall.validationHour),
          this.user.mall.validationDay,
          {weekStartsOn: 1}
        )
    },

    validationTimeReached () {
      return this.validationTime && isAfter(new Date(), this.validationTime)
    },
    getPeriodString () {
      return this.firstEntryDate && this.firstEntryDate.toLocaleString('fr-FR', {day: 'numeric', month: 'long'})
          + ' au '
          + endOfWeek(this.firstEntryDate, {weekStartsOn: 1}).toLocaleString('fr-FR', {day: 'numeric', month: 'long'})
          + ', Semaine '
          + getWeek(this.firstEntryDate, { weekStartsOn: 1, firstWeekContainsDate: 4 })
    },
    getValidationString () {
      return this.validationTime && `Validation à partir de ${this.validationTime.toLocaleString('fr-FR', {weekday: 'long', hour: 'numeric'})}`
    }
  },
  validations() {
    const internalEntries = {
      $each: {}
    }
    for (const field of this.fields) {
      internalEntries.$each[field.key] = {
        required: field.required ? required : false,
        integer: field.type === 'number' ? integer : false,
        decimal: field.type === 'ca' ? decimal : false,
        minValue: field.type === 'number' || field.type === 'ca' ? minValue(0) : false
      }
    }
    return {internalEntries}
  },
  methods: {
    closeModal() {
      this.showConfirmationModal = false;
    },
    validForm () {
      let isValid = this.formValidationStatus.valid
      this.internalEntries.forEach((entry, index) => {
        if (entry.isUserOpen && this.$v.internalEntries.$each.$iter[index].$invalid) isValid = this.formValidationStatus.invalid

      })

      if (isValid === this.formValidationStatus.valid) {
        isValid = this.formValidationStatus.suspicious
      }

      return isValid
    },

    async saveData () {
      this.closeModal();
      if (true === this.sending) return
      this.$v.$touch()
      if (this.validForm()) {
        try {
          let requests = []
          for (const entry of this.internalEntries) {
            requests.push(Entries.updateEntry(entry))
          }
          await Promise.all(requests)
          this.edited = false
          this.$v.$reset()
          this.$emit('saved')
          this.$store.dispatch('successMessage', 'Les données ont bien été mises à jour')
        } catch (error) {
          this.$store.dispatch('errorMessage', 'Erreur survenue lors de la mise à jour des données')
          throw error
        }
      } else {
        this.$store.dispatch('errorMessage', 'Saisie incorrect, veuillez vérifier les champs en surbrillance.')
      }
    },

    async testAndSendData () {
      if (!this.hasSuspiciousField) {
        await this.sendData();
        return;
      }

      this.showConfirmationModal = true;
    },

    async sendData () {
      if (true === this.sending) return
      this.$v.$touch()
      if (this.validForm()) {
        try {
          this.sending = true
          let requests = []
          for (const entry of this.internalEntries) {
            requests.push(Entries.updateEntry(entry))
          }
          await Promise.all(requests)
          this.edited = false
          this.sending = false
          await Entries.send(this.user.id)
          this.$v.$reset()
          this.$emit('sent')
          this.$store.dispatch('successMessage', 'Les données ont bien été envoyées')
        } catch (error) {
          this.sending = false
          this.$store.dispatch('errorMessage', 'Erreur survenue durant l\'envoie des données')
          throw error
        }
      } else {
        this.$store.dispatch('errorMessage', 'Saisie incorrect, veuillez vérifier les champs en surbrillance.')
      }
    },

    formatEntry (entry) {
      const entryFormatted = {...entry}
      for (const field of this.fields) {
        if (field.type === 'ca') {
          const ca = Number.parseFloat(entryFormatted[field.key])
          entryFormatted[field.key] = ca.toFixed(2)
        }
      }
      return entryFormatted
    }
  }
}
</script>
